import React from "react";

import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
} from "@mui/material";

import { Meeting, Organisation, User } from "../../../types";
import TableViewShell from "../../../components/views/table-view-shell";
import PatientMeetingsTableRow from "./patient-meetings-table-row";

type PatientMeetingsTableProps = {
  filteredMeetings: Meeting[];
  unfilteredMeetingsCount: number;
  isLoading: boolean;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (field: string) => void;
  userDictionary: Record<string, User>;
  teamDictionary: Record<string, Organisation>;
  openEdit: (meeting: Meeting) => void;
  deleteButtonClick: (meeting: Meeting) => void;
};

const PatientMeetingsTable: React.FC<PatientMeetingsTableProps> = ({
  filteredMeetings,
  unfilteredMeetingsCount,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  userDictionary,
  teamDictionary,
  openEdit,
  deleteButtonClick,
}) => {
  const columns = (
    <colgroup>
      <col style={{ width: 120 }} />
      <col style={{ width: 150 }} />
      <col style={{ width: 150 }} />
      <col style={{ width: 220 }} />
      <col />
      <col style={{ width: 96 }} />
    </colgroup>
  );

  return (
    <TableViewShell
    
      shownCount={filteredMeetings.length}
      totalCount={unfilteredMeetingsCount}
      columns={columns}
      headerAriaLabel="patient meetings table header"
      bodyAriaLabel="patient meetings table"
      header={
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={{ py: 0.5, width: 120, whiteSpace: "nowrap" }}>
              <TableSortLabel
                active={sortBy === "status"}
                direction={sortBy === "status" ? sortOrder : "asc"}
                onClick={() => onSort("status")}
                sx={{ fontSize: "0.875rem" }}
              >
                Meeting Status
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, width: 150, whiteSpace: "nowrap" }}>
              <TableSortLabel
                active={sortBy === "startTime"}
                direction={sortBy === "startTime" ? sortOrder : "asc"}
                onClick={() => onSort("startTime")}
                sx={{ fontSize: "0.875rem" }}
              >
                Start Time
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, width: 150, whiteSpace: "nowrap" }}>
              <TableSortLabel
                active={sortBy === "endTime"}
                direction={sortBy === "endTime" ? sortOrder : "asc"}
                onClick={() => onSort("endTime")}
                sx={{ fontSize: "0.875rem" }}
              >
                End Time
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5, width: 220, whiteSpace: "nowrap" }}>
              <TableSortLabel
                active={sortBy === "creatorId"}
                direction={sortBy === "creatorId" ? sortOrder : "asc"}
                onClick={() => onSort("creatorId")}
                sx={{ fontSize: "0.875rem" }}
              >
                Creator
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5, fontSize: "0.875rem", width: "100%", minWidth: 260 }}>
              Attendees
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5, fontSize: "0.875rem", width: 96, whiteSpace: "nowrap" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
      }
      body={
        <TableBody>
          {isLoading ? (
            Array.from(new Array(5)).map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
              </TableRow>
            ))
          ) : (
            filteredMeetings.map((meeting, index) => (
              <React.Fragment key={`${meeting.meetingId}${index}`}>
                <PatientMeetingsTableRow
                  meeting={meeting}
                  users={userDictionary}
                  teams={teamDictionary}
                  openEdit={openEdit}
                  deleteButtonClick={deleteButtonClick}
                />
              </React.Fragment>
            ))
          )}
        </TableBody>
      }
    />
  );
};

export default PatientMeetingsTable;